import { gql, QueryHookOptions, useMutation, useQuery } from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import { QueryVariables, Records } from 'src/graphql/types';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { BrandGroupRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import { emptyStringsToNull, removeNotDefinedProperties } from 'src/utils/objects';

export const BRAND_GROUP_FIELDS = gql`
  fragment BrandGroupFields on BrandGroup {
    id
    brandGroupId
    brandGroupName
    supportEmail
    membershipEmail
    supportEmailSignature
    membershipEmailSignature
    internalEmail
    fwdPhoneSupport
    fwdPhoneSignature
    fwdPhoneTwilio
    callerIdPhone
    fwdPhoneReroute
    fwdPhoneRerouteExt
    fwdPhonePublished
    fwdPhonePublishedExt
  }
`;

export const GET_BRAND_GROUPS_QUERY = gql`
  query GetBrandGroup($params: QueryParams) @entity(name: "BrandGroup") {
    getBrandGroup(queryParams: $params) {
      records {
        ...BrandGroupFields
      }
    }
  }
  ${BRAND_GROUP_FIELDS}
`;

const UPDATE_BRAND_GROUP_MUTATION = gql`
  mutation UpdateBrandGroup($conditions: [ConditionInput], $data: BrandGroupDataInput) {
    updateBrandGroup(conditions: $conditions, data: $data) {
      ...BrandGroupFields
    }
  }
  ${BRAND_GROUP_FIELDS}
`;

interface GetBrandGroups {
  getBrandGroup: Records<BrandGroupRaw>;
}

export const useBrandGroups = (
  partnerId: number | null,
  options?: QueryHookOptions<GetBrandGroups>,
) => {
  const { data, ...rest } = useQuery<GetBrandGroups, QueryVariables>(GET_BRAND_GROUPS_QUERY, {
    ...options,
    skip: isNullish(partnerId),
    variables: {
      params: {
        filters: [
          {
            attribute: 'partnerId',
            operator: '=',
            value: String(partnerId),
          },
        ],
        sorting: { attribute: 'brandGroupName' },
      },
    },
  });

  const brandGroups = useMemo(
    () => data?.getBrandGroup.records || [],
    [data?.getBrandGroup.records],
  );

  return { brandGroups, ...rest };
};

export const useBrandGroup = (partnerId: number | null) => {
  const { showErrorAlert } = useNotifications();

  const [updateBrandGroupMutation] = useMutation<{
    updateBrandGroup: BrandGroupRaw[];
  }>(UPDATE_BRAND_GROUP_MUTATION, {
    onError: (error) => {
      // A save superseded by a newer one is aborted on purpose; nothing went wrong.
      if (error.networkError?.name === 'AbortError') {
        return;
      }

      console.error(error);
      const message = 'Something went wrong updating the brand group, please try again';
      showErrorAlert(message);
    },
  });

  const updateBrandGroup = useCallback(
    async (brandGroup: BrandGroupUpdatePatch, options?: { signal?: AbortSignal }) => {
      if (!brandGroup.id) {
        return;
      }

      // Clearing a text field should persist as a true null rather than '',
      // so empty strings are normalized to null before sending the patch.
      const patch = emptyStringsToNull(
        removeNotDefinedProperties({
          supportEmail: brandGroup.supportEmail,
          membershipEmail: brandGroup.membershipEmail,
          supportEmailSignature: brandGroup.supportEmailSignature,
          membershipEmailSignature: brandGroup.membershipEmailSignature,
          internalEmail: brandGroup.internalEmail,
          fwdPhoneSupport: brandGroup.fwdPhoneSupport,
          fwdPhoneSignature: brandGroup.fwdPhoneSignature,
          fwdPhoneTwilio: brandGroup.fwdPhoneTwilio,
          callerIdPhone: brandGroup.callerIdPhone,
          fwdPhoneReroute: brandGroup.fwdPhoneReroute,
          fwdPhoneRerouteExt: brandGroup.fwdPhoneRerouteExt,
          fwdPhonePublished: brandGroup.fwdPhonePublished,
          fwdPhonePublishedExt: brandGroup.fwdPhonePublishedExt,
        }),
      );

      // Reuse the normalized patch so the optimistic entity reflects the same
      // null values that are sent to the server (not the raw '' form values).
      const optimisticResponseData = {
        id: brandGroup.id,
        partnerId: brandGroup.partnerId!,
        supportEmail: brandGroup.supportEmail!,
        membershipEmail: brandGroup.membershipEmail!,
        internalEmail: brandGroup.internalEmail!,
        supportEmailSignature: brandGroup.supportEmailSignature!,
        membershipEmailSignature: brandGroup.membershipEmailSignature!,
        brandGroupId: brandGroup.brandGroupId!,
        brandGroupName: brandGroup.brandGroupName!,
        fwdPhoneSupport: brandGroup.fwdPhoneSupport!,
        fwdPhoneSignature: brandGroup.fwdPhoneSignature!,
        fwdPhoneTwilio: brandGroup.fwdPhoneTwilio!,
        callerIdPhone: brandGroup.callerIdPhone!,
        fwdPhonePublished: brandGroup.fwdPhonePublished!,
        fwdPhonePublishedExt: brandGroup.fwdPhonePublishedExt!,
        fwdPhoneReroute: brandGroup.fwdPhoneReroute!,
        fwdPhoneRerouteExt: brandGroup.fwdPhoneRerouteExt!,
        brands: brandGroup.brands!,
        createdAt: brandGroup.createdAt!,
        updatedAt: brandGroup.updatedAt!,
        partner: brandGroup.partner!,
        labelAssignments: brandGroup.labelAssignments!,
        ...patch,
      } as BrandGroupRaw;

      const { data } = await updateBrandGroupMutation({
        variables: {
          conditions: [
            {
              attribute: 'id',
              operator: '=',
              value: brandGroup.id,
            },
          ],
          data: patch,
        },
        context: { fetchOptions: { signal: options?.signal } },
        optimisticResponse: {
          updateBrandGroup: [optimisticResponseData],
        },
        update: (cache, { data: response }) => {
          const updated = response?.updateBrandGroup?.[0];
          if (!updated) return;

          const id = cache.identify({
            __typename: 'BrandGroup',
            id: updated.brandGroupId,
          });

          cache.modify({
            id,
            fields: generateFieldModifiersMap({
              ...updated,
              __typename: 'BrandGroup',
            }),
          });

          cache.updateQuery<GetBrandGroups, QueryVariables>(
            {
              query: GET_BRAND_GROUPS_QUERY,
              variables: {
                params: {
                  filters: [
                    {
                      attribute: 'partnerId',
                      operator: '=',
                      value: String(partnerId),
                    },
                  ],
                  sorting: { attribute: 'brandGroupName', desc: false },
                },
              },
            },
            (existingData) => {
              if (!existingData) return;

              const {
                getBrandGroup: { records },
              } = existingData;
              const updatedRecords = records.map((item) => {
                if (item.id === brandGroup.id) {
                  // Reuse the canonical updated entity so cleared fields reflect
                  // the server's null value rather than the raw '' form value.
                  return {
                    ...item,
                    ...updated,
                  } as BrandGroupRaw;
                }

                return item;
              });
              return {
                getBrandGroup: {
                  __typename: 'BrandGroupDataResponse',
                  records: updatedRecords,
                },
              };
            },
          );
        },
      });

      return data?.updateBrandGroup[0];
    },
    [updateBrandGroupMutation, partnerId],
  );

  return {
    updateBrandGroup,
  };
};

type NullablePhoneFields =
  | 'fwdPhoneSupport'
  | 'fwdPhoneSignature'
  | 'fwdPhoneTwilio'
  | 'callerIdPhone'
  | 'fwdPhoneReroute'
  | 'fwdPhonePublished'
  | 'fwdPhonePublishedExt'
  | 'fwdPhoneRerouteExt';

export type BrandGroupUpdatePatch = Partial<
  Omit<BrandGroupRaw, NullablePhoneFields> & Record<NullablePhoneFields, number | null>
>;
